<template>
    <div class="field--date" @click="setFocusOnDateField">
        <div
            class="field--date__label"
            :style="{ opacity: isLabelShown ? 1 : 0 }"
        >
            {{ label }}
        </div>
        <div class="field--date__wrapper">
            <Field
                v-model="day"
                type="text"
                :max-length="2"
                :name="name"
                :placeholder="$t(`${TRANSLATION_KEY}.day`)"
                :handles-error="false"
                :is-validated="false"
                cy-data="day"
                :handles-data-preload="false"
                :only-numbers="true"
                :is-numeric="true"
                @blur="onDayBlur"
            />
            <div
                class="custom-select-wrapper position-relative month-wrapper"
                cy-data="month"
            >
                <Select
                    :key="i18nKey"
                    v-model="month"
                    :name="name"
                    :item-list="months"
                    :placeholder="$t(`${TRANSLATION_KEY}.month`)"
                    :is-validated="false"
                    :handles-data-preload="false"
                />
            </div>
            <Field
                v-model="year"
                type="text"
                class="input-wrapper--year-of-birth"
                :name="name"
                :max-length="4"
                :placeholder="$t(`${TRANSLATION_KEY}.year`)"
                :handles-error="false"
                :is-validated="false"
                cy-data="year"
                :handles-data-preload="false"
                :only-numbers="true"
                :is-numeric="true"
                @blur="onBlur"
            />
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted } from 'vue';
import Field from '@/components/basic/fields/Field.vue';
import Select from '@/components/basic/fields/Select.vue';
import { DataHelper } from '@/helpers/DataHelper';
import i18n from '@/i18n';

export default defineComponent({
    name: 'DateField',
    components: {
        Field,
        Select,
    },
    props: {
        name: {
            type: String,
            default: '',
        },
        modelValue: {
            type: String,
            default: '',
        },
        label: {
            type: String,
            default: '',
        },
    },
    emits: ['update:modelValue', 'blur'],
    setup(props, { emit }) {
        const TRANSLATION_KEY = 'compositeFields.DateField';

        const months = ref<Array<{ key: string; value: string }>>([]);

        const loadMonthsTranslations = async () => {
            const translations = await DataHelper.loadJSON(
                i18n.global.locale.value
            );
            months.value = translations.months || [];
        };

        const i18nReady = computed(() => {
            const msg = i18n.global.getLocaleMessage(i18n.global.locale.value);
            return msg && Object.keys(msg).length > 0;
        });

        watch(
            [() => i18n.global.locale.value, i18nReady],
            async ([, ready]) => {
                if (ready) await loadMonthsTranslations();
            },
            { immediate: true }
        );

        const day = ref('');
        const month = ref('');
        const year = ref('');

        const isInitialLoad = ref(true);

        const isInputFocused = ref(false);
        const isLabelShown = computed(() => {
            return (
                day.value || month.value || year.value || isInputFocused.value
            );
        });

        const i18nKey = computed(
            () => `${i18n.global.locale.value}-${months.value.length}`
        );

        const setFocusOnDateField = () => {
            isInputFocused.value = true;
        };
        const blurFocusOnDateField = (evt: MouseEvent) => {
            if (!(evt.target as HTMLElement).closest('.field--date')) {
                isInputFocused.value = false;
            }
        };

        const onBlur = () => {
            emit('blur');
        };

        const onDayBlur = () => {
            if (isFilled()) {
                emit('blur');
            }
        };

        onMounted(async () => {
            await loadMonthsTranslations();

            if (props.modelValue && props.modelValue.includes('-')) {
                const [y, m, d] = props.modelValue.split('-');
                year.value = y;
                month.value = parseInt(m, 10).toString();
                day.value = d;

                if (isInitialLoad.value) {
                    if (day.value && month.value && year.value) {
                        emit('blur');
                    }
                }
            }

            isInitialLoad.value = false;

            document.addEventListener('click', blurFocusOnDateField);
        });

        watch(
            () => props.modelValue,
            (newVal) => {
                if (isInitialLoad.value) return;

                if (newVal && newVal.includes('-')) {
                    const [y, m, d] = newVal.split('-');
                    year.value = y;
                    month.value = parseInt(m, 10).toString();
                    day.value = d.replace(/^0+/, '');
                }
            }
        );

        const isFilled = () => {
            return day.value && month.value && year.value;
        };

        const updateModelValue = () => {
            if (isFilled()) {
                const paddedDay = day.value.padStart(2, '0');
                const paddedMonth = month.value.padStart(2, '0');
                const newValue = `${year.value}-${paddedMonth}-${paddedDay}`;

                emit('update:modelValue', newValue);
            } else {
                emit('update:modelValue', '');
            }
        };

        watch(day, updateModelValue);
        watch(month, () => {
            updateModelValue();
            if (isFilled()) {
                emit('blur');
            }
        });
        watch(year, updateModelValue);

        return {
            TRANSLATION_KEY,
            months,
            day,
            month,
            year,
            isLabelShown,
            setFocusOnDateField,
            onBlur,
            onDayBlur,
            i18nKey,
        };
    },
});
</script>
